import { useEffect, useMemo } from "react";
import { useFeatureFlag } from "@jsq/flags-react";
import type { ReportType, SupportedTemplateType } from "~/graphql/supergraphTypes";

export type { ReportType };

const REPORT_TYPE_TO_SUPPORTED: Readonly<Record<ReportType, SupportedTemplateType>> = {
  ACCOUNT_STATEMENT: "ACCOUNT_STATEMENT",
  CAPITAL_ACTIVITY_NOTICE: "CAPITAL_ACTIVITY_NOTICE",
  CAPITAL_CALL_NOTICE: "CAPITAL_CALL_NOTICE",
  DISTRIBUTION_NOTICE: "DISTRIBUTION_NOTICE",
  INVESTOR_REPORT: "INVESTOR_REPORT",
  MANAGEMENT_FEE_NOTICE: "MANAGEMENT_FEE_NOTICE",
  NAV_STATEMENT: "NAV_STATEMENT",
  NET_INCOME_NOTICE: "NET_INCOME_NOTICE",
};

/**
 * Map a `ReportType` (template-level enum) to the matching `SupportedTemplateType`
 * (query-level enum). Returns `null` when no template type is selected — callers
 * should treat this as "no compatible queries".
 */
export const toSupportedTemplateType = (
  reportType: ReportType | "",
): SupportedTemplateType | null => {
  if (reportType === "") return null;
  return REPORT_TYPE_TO_SUPPORTED[reportType];
};

export type PreviewEntityInfo = {
  id: string;
  name: string;
  type: string;
  status: string;
};

export type DrawerTab = "instructions" | "query-reference";
export type DrawerMode = "query-only" | "full";

export type ReportTypeOption = {
  value: ReportType;
  label: string;
};

type VersionWithCreatedAt = { createdAt: string };

export const getLatestVersion = <T extends VersionWithCreatedAt>(
  currentVersion: T | null | undefined,
  versions: T[] | null | undefined,
): T | null => {
  if (currentVersion) return currentVersion;
  if (!versions || versions.length === 0) return null;
  let latest = versions[0];
  let latestTime = new Date(latest.createdAt).getTime();
  for (let i = 1; i < versions.length; i++) {
    const time = new Date(versions[i].createdAt).getTime();
    if (time > latestTime) {
      latest = versions[i];
      latestTime = time;
    }
  }
  return latest;
};

export type PreviewSelectOption = {
  id: string;
  name: string;
};

/** Form / mutation values use GraphQL `ReportType` enum strings (e.g. `CAPITAL_CALL_NOTICE`). */
export const REPORT_TYPE_CONFIG: ReportTypeOption[] = [
  { value: "ACCOUNT_STATEMENT", label: "Account statement" },
  { value: "CAPITAL_ACTIVITY_NOTICE", label: "Capital activity notice" },
  { value: "CAPITAL_CALL_NOTICE", label: "Capital call notice" },
  { value: "DISTRIBUTION_NOTICE", label: "Distribution notice" },
  { value: "INVESTOR_REPORT", label: "Investor report" },
  { value: "MANAGEMENT_FEE_NOTICE", label: "Management fee notice" },
  { value: "NAV_STATEMENT", label: "Net asset value" },
  { value: "NET_INCOME_NOTICE", label: "Net income notice" },
];

export const REPORT_TYPE_ENUM_VALUES: readonly ReportType[] = REPORT_TYPE_CONFIG.map(
  (c) => c.value,
);

export const isReportTypeEnumValue = (value: string): value is ReportType =>
  REPORT_TYPE_ENUM_VALUES.includes(value as ReportType);

export const requireReportTypeEnum = (value: string): ReportType => {
  if (!isReportTypeEnumValue(value)) {
    throw new Error("Template type must be one of the supported report types");
  }
  return value;
};

/**
 * Returns the report type options that should be shown in a "template type"
 * dropdown, based on the current feature-flag state.
 *
 * - `ACCOUNT_STATEMENT` is always included.
 * - `CAPITAL_CALL_NOTICE` is included only when `customCapitalCallNotice` is `true`.
 * - `DISTRIBUTION_NOTICE` is included only when `customDistributionNotice` is `true`.
 * - All other report types are excluded.
 */
export const useAvailableReportTypeOptions = (): ReportTypeOption[] => {
  const isAccountStatementEnabled = useFeatureFlag("reportsPDFM1", false);
  const isCapitalCallEnabled = useFeatureFlag("customCapitalCallNotice", false);
  const isDistributionEnabled = useFeatureFlag("customDistributionNotice", false);

  // TODO(RPRT-4164): temporary debug logging for preview feature-flag issue — remove before merge.
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("[RPRT-4164] report type feature flags", {
      reportsPDFM1: isAccountStatementEnabled,
      customCapitalCallNotice: isCapitalCallEnabled,
      customDistributionNotice: isDistributionEnabled,
    });
  }, [isAccountStatementEnabled, isCapitalCallEnabled, isDistributionEnabled]);

  return useMemo(
    () =>
      REPORT_TYPE_CONFIG.filter((option) => {
        if (option.value === "ACCOUNT_STATEMENT") return isAccountStatementEnabled;
        if (option.value === "CAPITAL_CALL_NOTICE") return isCapitalCallEnabled;
        if (option.value === "DISTRIBUTION_NOTICE") return isDistributionEnabled;
        return false;
      }),
    [isAccountStatementEnabled, isCapitalCallEnabled, isDistributionEnabled],
  );
};

const TRANSACTION_BASED_REPORT_TYPES: ReadonlySet<ReportType> = new Set([
  "CAPITAL_CALL_NOTICE",
  "DISTRIBUTION_NOTICE",
  "MANAGEMENT_FEE_NOTICE",
  "NET_INCOME_NOTICE",
]);

/**
 * Returns true for report types that are driven by transaction batches
 * (Capital Call, Distribution, Management Fee, Net Income notices),
 * as opposed to account-statement-style reports that target a single account.
 */
export const isTransactionBasedReport = (reportType: string): boolean =>
  TRANSACTION_BASED_REPORT_TYPES.has(reportType as ReportType);

export const getReportTypeLabel = (value: string | null | undefined): string => {
  if (value == null || value === "") {
    return "-";
  }
  return REPORT_TYPE_CONFIG.find((c) => c.value === value)?.label ?? value;
};
